import { useCallback, useEffect, useRef, useState } from 'react'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { flashEffect } from '@renderer/utils/flashUtils'
import { backgroundFilterProcessor } from '@renderer/utils/backgroundFilterProcessor'
import { generateUUID } from '@renderer/utils/idUtils'
import { isDigitalMode } from '@renderer/lib/runtime/mode'
import { useCameraRecovery } from '@renderer/pages/View'
import { BK_CAPTURE_RATIO, slotCountOf } from './cutCategories'
import { startCaptureLoop } from './captureLoop'

export type BkCapturePhase = 'prep' | 'counting' | 'shooting' | 'review' | 'stalled'

/**
 * Queue the filter bake for one photograph — only the filter the guest chose.
 *
 * BK's guest picks the filter BEFORE the shutter and cannot change it after,
 * so baking every project filter only put the one that matters behind the
 * others in a one-at-a-time queue: the strip sat in colour for several shots.
 * With no filter chosen, fall back to baking them all, as stock does.
 */
function bake(photoUrl: string, photoId: string): void {
  const filterId = useSessionStateStore.getState().userPreferences?.filterId
  if (filterId) void backgroundFilterProcessor.addToQueue(photoUrl, photoId, filterId)
  else void backgroundFilterProcessor.processAllFiltersForPhoto(photoUrl, photoId)
}

/**
 * Consecutive shutter failures before the loop stops trying.
 *
 * Without this the loop is infinite: a camera that cannot shoot fails, the
 * counter does not advance, `advance` restarts the countdown, and the guest
 * watches 3-2-1 forever with an empty strip and no way out. Three attempts is
 * enough to ride out a transient USB blip and short enough that a real fault
 * surfaces while somebody is still standing there.
 */
const MAX_CONSECUTIVE_FAILURES = 2

/**
 * Hard ceiling on one shutter call.
 *
 * `unifiedCameraUtils.captureImage` awaits `wakeCamera` and the driver, and on a
 * booth whose camera was never connected for capture it can simply never
 * resolve. An unbounded await is invisible: the phase sits in `shooting`, the
 * last countdown number stays frozen on screen, and the catch block never runs,
 * so nothing reaches the log either. `view/FrameBasedCapture` races every camera
 * call against a timeout for the same reason — this matches it.
 */
const SHUTTER_TIMEOUT_MS = 8000

/**
 * The Bingkai Kenangan capture loop.
 *
 * Split out of the page so the screen stays a plate plus overlays: the timing,
 * the camera and the retake bookkeeping are all here, and `Capture.tsx` only
 * decides where things sit on the 1920x1080 artwork.
 *
 * How many photographs a session takes is NOT a project setting. It is the
 * number of unique `photo-N` slots in the frame the guest committed to on the
 * cut screen, which is the same rule `view/Capture.tsx` applies in
 * `frame-based` mode. Four-cut shoots four, eight-cut shoots eight, and nobody
 * has to keep a separate counter in sync.
 */
export function useBkCapture(): {
  phase: BkCapturePhase
  shotCount: number
  taken: number
  tick: number
  retakeMany: (indexes: number[]) => void
  retakingIndex: number | null
  /**
   * Slots whose retake has been asked for and not yet delivered, in the order
   * they will be shot. A slot leaves this list only when its new photograph
   * lands, so the page can keep marking it until then.
   */
  pendingRetakes: number[]
  canRetake: boolean
  /** Why the loop stopped, when it stopped. Empty while things are working. */
  error: string
  /** Try the shutter again after a stall. */
  retryShutter: () => void
} {
  const { setSessionState, logActivity, userPreferences } = useSessionStateStore()
  const { config, frames } = useConfigStore()
  const { cameraSettings, downloadLocal } = useSettingsStore()
  const cameraRecovery = useCameraRecovery()

  const captureSettings = config?.pages?.capture?.settings
  const retakeSettings = config?.pages?.retake?.settings

  const frame = frames.find((f) => f._id === userPreferences?.frameId)
  const shotCount = Math.max(slotCountOf(frame), 1)

  const [phase, setPhase] = useState<BkCapturePhase>('prep')
  const [taken, setTaken] = useState(0)
  const [tick, setTick] = useState(0)
  const [retakingIndex, setRetakingIndex] = useState<number | null>(null)
  const [pendingRetakes, setPendingRetakes] = useState<number[]>([])
  const [error, setError] = useState('')
  const failuresRef = useRef(0)

  // Read in async callbacks that must not close over a stale render.
  const takenRef = useRef(0)
  const retakingRef = useRef<number | null>(null)
  /** Slots still waiting to be retaken after the current one. */
  const retakeQueueRef = useRef<number[]>([])
  const busyRef = useRef(false)

  const prepSeconds = captureSettings?.frameBasedPrepTimeout ?? captureSettings?.prepTimeout ?? 3
  const shotSeconds =
    captureSettings?.frameBasedCaptureCountdown ?? captureSettings?.captureCountdown ?? 3

  const prep = useCountdown(Math.max(prepSeconds, 1))
  const shot = useCountdown(Math.max(shotSeconds, 1), { audioCue: true })

  /**
   * Always BK's canonical ratio, never the booth's `captureRatio` setting and
   * never the committed frame's own window: the border is chosen after the
   * shutter, so a per-frame crop would be a guess at which one the guest is
   * about to pick. See BK_CAPTURE_RATIO.
   */
  const normalizedRatio = useCallback((): number => BK_CAPTURE_RATIO, [])

  /** One photograph: flash, shutter, store, queue the filter bake. */
  const takeOne = useCallback(async (): Promise<void> => {
    if (busyRef.current) return
    busyRef.current = true
    setPhase('shooting')

    try {
      flashEffect('.bk-flash')
      // Let the flash paint before the shutter blocks the main thread.
      await new Promise((r) => setTimeout(r, 180))

      const blob = await Promise.race([
        unifiedCameraUtils.captureImage(
          (userPreferences?.selectedCamera as 'primary' | 'secondary') ?? 'primary',
          undefined,
          normalizedRatio(),
          !cameraSettings.useShutterCapture
        ),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('The camera did not respond')), SHUTTER_TIMEOUT_MS)
        )
      ])

      if (!blob) {
        failuresRef.current += 1
        logActivity('BK capture failed: no image data', { level: 'error' })
        setError('The camera returned no image')
        return
      }

      const photoUrl = URL.createObjectURL(blob)

      // Each raw photograph is saved on the booth as it is taken, as stock
      // Capture does: the local copy that survives a failed upload or a crash
      // later in the session. Off in digital mode, which has no booth disk.
      if (downloadLocal && !isDigitalMode()) {
        const sessionId = useSessionStateStore.getState().id
        const filename = `dreambooth-${sessionId}-${Date.now()}.jpg`
        const a = document.createElement('a')
        a.href = photoUrl
        a.download = filename
        a.setAttribute('data-automated-click', 'true')
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        logActivity('Downloaded photo', { type: 'photo', filename })
      }
      const items = [...(useSessionStateStore.getState().gallery?.capturedItems ?? [])]
      const at = retakingRef.current

      if (at !== null && items[at]) {
        const previous = items[at].photoUrl
        if (previous?.startsWith('blob:')) URL.revokeObjectURL(previous)
        // A NEW item, not the old one with its photoUrl swapped. The old one
        // carries `originalPhotoUrl` — the previous shot, set by its first
        // filter bake — and the bake reads that before photoUrl, so the retaken
        // slot was re-filled with the photograph it replaced (from a blob URL
        // just revoked, when that failed). A fresh id also strands any bake
        // still queued for the old shot: it finds no item and writes nothing.
        items[at] = { id: generateUUID(), photoUrl, timestamp: new Date() }
        setSessionState({ gallery: { capturedItems: items, filterCache: {} } })
        bake(photoUrl, items[at].id)
        logActivity(`BK retook photo ${at + 1}`)
        retakingRef.current = null
        setRetakingIndex(null)
        // Only now has this slot actually been replaced.
        setPendingRetakes((prev) => prev.filter((i) => i !== at))
      } else {
        const item = { id: generateUUID(), photoUrl, timestamp: new Date() }
        setSessionState({ gallery: { capturedItems: [...items, item] } })
        bake(photoUrl, item.id)
        takenRef.current += 1
        setTaken(takenRef.current)
        logActivity(`BK captured photo ${takenRef.current} of ${shotCount}`)
      }
      // A frame arrived, so whatever went wrong before was transient.
      failuresRef.current = 0
      setError('')
    } catch (err) {
      failuresRef.current += 1
      const message = err instanceof Error ? err.message : 'unknown camera error'
      logActivity(`BK capture failed: ${message}`, { level: 'error' })
      setError(message)
    } finally {
      busyRef.current = false
    }
  }, [
    cameraSettings.useShutterCapture,
    downloadLocal,
    logActivity,
    normalizedRatio,
    setSessionState,
    shotCount,
    userPreferences?.selectedCamera
  ])

  /** Queue the next shot, or stop — for review, or because the camera is not working. */
  const advance = useCallback((): void => {
    if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
      // Stop rather than count at a guest forever behind a camera that cannot
      // shoot, and hand the booth to the same operator recovery prompt every
      // other page uses instead of inventing a second one.
      setPhase('stalled')
      cameraRecovery?.onCameraRecoveryRequest?.(() => {
        failuresRef.current = 0
        setError('')
        advanceRef.current()
      })
      return
    }
    if (retakingRef.current === null && retakeQueueRef.current.length > 0) {
      const [next, ...rest] = retakeQueueRef.current
      retakeQueueRef.current = rest
      retakingRef.current = next
      setRetakingIndex(next)
    }
    if (retakingRef.current !== null || takenRef.current < shotCount) {
      shotRef.current.setRemainingSeconds(Math.max(shotSeconds, 1))
      setTick(Math.max(shotSeconds, 1))
      setPhase('counting')
      // Focus during the countdown so the shutter is not waiting on autofocus.
      unifiedCameraUtils.prefocus()
      shotRef.current.start()
    } else {
      setPhase('review')
    }
  }, [shotSeconds, shotCount])

  /**
   * `advance` reaches the prep countdown through a ref, never through a closure.
   *
   * The catalogue can still be loading when this mounts, and until the frame
   * resolves `shotCount` is 1. A callback captured at that moment would end the
   * session after a single photograph on an eight-cut booth — so the prep
   * effect calls whatever `advance` is current when the beat actually ends.
   */
  /**
   * The countdowns are reached through refs, never through effect dependencies.
   *
   * `useCountdown` returns a NEW OBJECT on every render. Listing it as a
   * dependency therefore re-runs the effect on every render — and a cleanup that
   * stops the countdown then kills the one just started, one render later. That
   * is what froze this page: the prep beat was stopped before its first tick,
   * `startedRef` blocked any restart, `advance` never ran, the shutter was never
   * reached, and the last number stayed painted on the preview with nothing in
   * the log. Two "fixes" downstream of it changed nothing, because the loop had
   * never started.
   */
  const prepRef = useRef(prep)
  prepRef.current = prep
  const shotRef = useRef(shot)
  shotRef.current = shot

  const advanceRef = useRef(advance)
  advanceRef.current = advance
  const takeOneRef = useRef(takeOne)
  takeOneRef.current = takeOne

  // Don't start counting at a guest until the frame — and with it the real shot
  // count — is known.
  const frameReady = Boolean(frame)
  const startedRef = useRef(false)

  useEffect(() => {
    if (!frameReady || startedRef.current) return
    startedRef.current = true

    // prepTimeout 0 means "no prep beat" — the session goes straight to the
    // first shot's countdown. startCaptureLoop wires both clocks before either
    // can start; see it for why that order matters.
    startCaptureLoop({
      prep: prepRef.current,
      shot: shotRef.current,
      prepSeconds,
      setTick,
      advance: () => advanceRef.current(),
      takeOne: () => takeOneRef.current()
    })
  }, [frameReady, prepSeconds])

  // Stop the clocks when the page goes away — and ONLY then.
  useEffect(
    () => (): void => {
      prepRef.current.stop()
      shotRef.current.stop()
    },
    []
  )

  /**
   * `maxRetakeSession` is null on this project, and `?? 0` turned that into
   * "no retakes ever", greying the swan for the whole session. Fall back to the
   * per-photo allowance, which is what the dashboard actually sets.
   */
  const maxRetakes = retakeSettings?.maxRetakeSession ?? retakeSettings?.maxRetakeCount ?? 3
  const canRetake = (userPreferences?.retakeCount ?? 0) < maxRetakes

  /**
   * Queue one or more slots to be retaken.
   *
   * The plate has a single retake swan, so the guest picks the photographs they
   * are unhappy with and the swan replaces all of them in turn. The queue lives
   * in a ref because `advance` reads it from inside a countdown callback.
   */
  const retakeMany = useCallback(
    (indexes: number[]): void => {
      if (!canRetake || busyRef.current || indexes.length === 0) return
      const ordered = [...indexes].sort((a, b) => a - b)
      const [first, ...rest] = ordered
      setPendingRetakes(ordered)
      retakeQueueRef.current = rest
      retakingRef.current = first
      setRetakingIndex(first)
      setSessionState({ userPreferences: { retakeCount: (userPreferences?.retakeCount ?? 0) + 1 } })
      logActivity(`BK retake requested for ${indexes.length} photo(s)`)
      advance()
    },
    [advance, canRetake, logActivity, setSessionState, userPreferences?.retakeCount]
  )

  const retryShutter = useCallback((): void => {
    failuresRef.current = 0
    setError('')
    advance()
  }, [advance])

  return {
    phase,
    shotCount,
    taken,
    tick,
    retakeMany,
    retakingIndex,
    pendingRetakes,
    canRetake,
    error,
    retryShutter
  }
}
